import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon.jsx';
import { SignatureModal } from './SignatureModal.jsx';
import { LetterHistoryModal } from './LetterHistoryModal.jsx';
import { useLetters } from '../store/lettersStore.js';
import { useAccount } from '../store/accountStore.js';
import { usePdf } from '../store/pdfStore.js';
import { useT } from '../i18n/index.js';
import { generateLetterPdf, sha256Hex } from '../utils/pdfOps.js';
import { downloadBlob } from '../utils/storage.js';
import { useToast } from './Toast.jsx';

// Pull the city out of a sender address block — the first line that
// starts with a 4-5 digit postal code, e.g. "94300 Vincennes".
function deriveCity(senderText) {
  for (const ln of String(senderText || '').split('\n')) {
    const m = ln.trim().match(/^\d{4,5}\s+(.+)$/);
    if (m) return m[1].trim();
  }
  return '';
}

function Field({ label, hint, children }) {
  return (
    <div className="letter-field">
      <span className="letter-label">{label}</span>
      {hint && <span className="tiny muted">{hint}</span>}
      {children}
    </div>
  );
}

export function LettersView() {
  const t = useT();
  const draft = useLetters((s) => s.draft);
  const editingId = useLetters((s) => s.editingId);
  const setField = useLetters((s) => s.setField);
  const setSignature = useLetters((s) => s.setSignature);
  const resetDraft = useLetters((s) => s.resetDraft);
  const commitToHistory = useLetters((s) => s.commitToHistory);
  const renameTemplate = useLetters((s) => s.renameTemplate);

  const senders = useAccount((s) => s.senders);
  const cities = useAccount((s) => s.cities);
  const recipients = useAccount((s) => s.recipients);
  const signatures = useAccount((s) => s.signatures);
  const accDefaults = useAccount((s) => s.defaults);
  const addItem = useAccount((s) => s.addItem);
  const setDefault = useAccount((s) => s.setDefault);
  const getDefault = useAccount((s) => s.getDefault);

  const accAttachments = useAccount((s) => s.attachments);
  const addExport = usePdf((s) => s.addExport);

  const push = useToast((s) => s.push);
  const [sigOpen, setSigOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // The city follows the sender address until the user edits it.
  const cityTouched = useRef(false);
  // Hidden file input for the "add attachments" button.
  const pjFileRef = useRef(null);
  // Index of the attachment row currently being dragged.
  const [dragIdx, setDragIdx] = useState(null);

  // Set the sender and, unless the user overrode the city, keep the
  // city in sync with the sender's address.
  const setSender = (text) => {
    setField('sender', text);
    if (!cityTouched.current) setField('city', deriveCity(text));
  };

  // Pre-fill an empty draft with the last-used account data. Runs on
  // mount and whenever the account defaults change; never overwrites a
  // draft the user has already started.
  useEffect(() => {
    const empty =
      !draft.sender && !draft.recipient && !draft.body &&
      !draft.city && !draft.subject && (draft.attachments || []).length === 0;
    if (!empty) return;
    const sender = getDefault('senders');
    const recipient = getDefault('recipients');
    const signature = getDefault('signatures');
    if (!sender && !recipient && !signature) return;
    resetDraft({
      sender: sender?.text || '',
      recipient: recipient?.text || '',
      // City defaults from the sender's address.
      city: sender ? deriveCity(sender.text) : '',
      signature: signature
        ? { dataUrl: signature.dataUrl, w: signature.w, h: signature.h }
        : null,
    });
  }, [
    accDefaults,
    draft.sender,
    draft.recipient,
    draft.body,
    draft.city,
    draft.subject,
    getDefault,
    resetDraft,
  ]);

  // Attachment list helpers. Always read the freshest list from the
  // store so rapid successive clicks don't clobber each other.
  const attachments = draft.attachments || [];
  const curAttachments = () => useLetters.getState().draft.attachments || [];
  const addAttachment = (label = '') =>
    setField('attachments', [...curAttachments(), { id: crypto.randomUUID(), label, file: null }]);
  // Add a row per picked file, label = file name without extension.
  const addAttachmentFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const rows = files.map((f) => ({
      id: crypto.randomUUID(),
      label: f.name.replace(/\.[^.]+$/, ''),
      file: f,
    }));
    setField('attachments', [...curAttachments(), ...rows]);
  };
  const updateAttachment = (id, patch) =>
    setField('attachments', curAttachments().map((a) => (a.id === id ? { ...a, ...patch } : a)));
  const removeAttachment = (id) =>
    setField('attachments', curAttachments().filter((a) => a.id !== id));
  // Reorder by index (used by drag & drop).
  const reorderAttachment = (from, to) => {
    if (from === to || from < 0 || to < 0) return;
    const list = [...curAttachments()];
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    setField('attachments', list);
  };

  const newLetter = () => {
    cityTouched.current = false;
    const sender = getDefault('senders');
    const recipient = getDefault('recipients');
    const signature = getDefault('signatures');
    resetDraft({
      sender: sender?.text || '',
      recipient: recipient?.text || '',
      city: sender ? deriveCity(sender.text) : '',
      signature: signature
        ? { dataUrl: signature.dataUrl, w: signature.w, h: signature.h }
        : null,
    });
  };

  const fileBase = () => {
    const raw = (draft.title || draft.subject || 'lettre').trim();
    return raw.replace(/[^\w\s-]/g, '').trim().slice(0, 60) || 'lettre';
  };

  // Record a generated letter in the shared export history, tagged so
  // the History panel shows it came from the letter tool.
  const recordInHistory = async (blob, name, templateId) => {
    try {
      const hash = await sha256Hex(blob);
      addExport(
        {
          id: crypto.randomUUID(),
          name,
          date: Date.now(),
          hash,
          size: blob.size,
          pages: 1,
          source: 'letters',
          // Links the export to its letter-template entry so the
          // history can re-open it in the letter editor.
          templateId: templateId || null,
        },
        blob,
        null
      );
    } catch {
      /* history is best-effort */
    }
  };

  // Generate the PDF and auto-save the letter to history (updating the
  // bound entry when editing one).
  const generate = async () => {
    setBusy(true);
    try {
      const blob = await generateLetterPdf(draft);
      const name = `${fileBase()}.pdf`;
      downloadBlob(blob, name);
      const tplId = commitToHistory();
      await recordInHistory(blob, name, tplId);
      push({ message: t('letters.toast_generated'), kind: 'success' });
    } catch (e) {
      console.error(e);
      push({ message: t('toast.error', { msg: e.message }), kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  // Save (or update) the current letter in history without downloading.
  const onSaveTemplate = () => {
    commitToHistory();
    push({ message: t('letters.toast_template_saved'), kind: 'success' });
  };

  // Re-generate the PDF of a stored history entry.
  const regenerate = async (tpl) => {
    setBusy(true);
    try {
      const blob = await generateLetterPdf({ ...tpl, signature: tpl.signature || null });
      const raw = (tpl.title || tpl.name || 'lettre').trim();
      const base = raw.replace(/[^\w\s-]/g, '').trim().slice(0, 60) || 'lettre';
      const name = `${base}.pdf`;
      downloadBlob(blob, name);
      await recordInHistory(blob, name, tpl.id);
      push({ message: t('letters.toast_generated'), kind: 'success' });
    } catch (e) {
      console.error(e);
      push({ message: t('toast.error', { msg: e.message }), kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const onRename = (tpl) => {
    const name = window.prompt(t('letters.template_name'), tpl.name || '');
    if (name === null || !name.trim()) return;
    renameTemplate(tpl.id, name.trim());
  };

  // Pick a saved account entry into a draft field.
  const pick = (kind, id, field, prop) => {
    if (!id) return;
    const list = { senders, cities, recipients, signatures }[kind];
    const item = list.find((x) => x.id === id);
    if (!item) return;
    setDefault(kind, id);
    if (kind === 'signatures') {
      setSignature({ dataUrl: item.dataUrl, w: item.w, h: item.h });
    } else if (kind === 'senders') {
      setSender(item.text); // also refreshes the city
    } else if (kind === 'cities') {
      cityTouched.current = true;
      setField('city', item.value);
    } else {
      setField(field, item[prop]);
    }
  };

  // Save the current value of a draft field into the account.
  const saveToAccount = (kind, field, multiline) => {
    const value = (draft[field] || '').trim();
    if (!value) return;
    if (multiline) {
      const label = window.prompt(t('account.label_ph'), value.split('\n')[0] || '');
      if (label === null) return;
      addItem(kind, { label: label.trim() || value.split('\n')[0], text: value });
    } else {
      addItem(kind, { value });
    }
    push({ message: t('account.toast_saved'), kind: 'success' });
  };

  return (
    <div className="letters-view">
      <div className="letters-head">
        <div className="letters-head-text">
          <h1>{t('letters.title')}</h1>
          <span className="sub">{t('letters.subtitle')}</span>
        </div>
        <button className="btn" onClick={() => setHistOpen(true)}>
          <Icon name="history" size={15} /> {t('letters.history')}
        </button>
      </div>

      <div className="letters-form">
          <Field label={t('letters.file_title')} hint={t('letters.file_title_hint')}>
            <input
              className="letter-input"
              value={draft.title}
              onChange={(e) => setField('title', e.target.value)}
              placeholder={t('letters.file_title_ph')}
            />
          </Field>

          <div className="letter-row2">
            <Field label={t('letters.sender')} hint={t('letters.sender_hint')}>
              <div className="letter-pick">
                <select
                  className="letter-input letter-pick-select"
                  value={accDefaults.senders || ''}
                  onChange={(e) => pick('senders', e.target.value, 'sender', 'text')}
                  aria-label={t('letters.sender')}
                >
                  <option value="">{t('letters.pick')}</option>
                  {senders.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
                </select>
                <button className="btn icon sm" onClick={() => saveToAccount('senders', 'sender', true)} disabled={!draft.sender.trim()} title={t('letters.save_to_account')} aria-label={t('letters.save_to_account')}>
                  <Icon name="plus" size={14} />
                </button>
              </div>
              <textarea
                className="letter-input"
                rows={4}
                value={draft.sender}
                onChange={(e) => setSender(e.target.value)}
              />
            </Field>
            <Field label={t('letters.recipient')} hint={t('letters.recipient_hint')}>
              <div className="letter-pick">
                <select
                  className="letter-input letter-pick-select"
                  value={accDefaults.recipients || ''}
                  onChange={(e) => pick('recipients', e.target.value, 'recipient', 'text')}
                  aria-label={t('letters.recipient')}
                >
                  <option value="">{t('letters.pick')}</option>
                  {recipients.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
                </select>
                <button className="btn icon sm" onClick={() => saveToAccount('recipients', 'recipient', true)} disabled={!draft.recipient.trim()} title={t('letters.save_to_account')} aria-label={t('letters.save_to_account')}>
                  <Icon name="plus" size={14} />
                </button>
              </div>
              <textarea
                className="letter-input"
                rows={4}
                value={draft.recipient}
                onChange={(e) => setField('recipient', e.target.value)}
              />
            </Field>
          </div>

          <div className="letter-row2">
            <Field label={t('letters.city')}>
              <div className="letter-pick">
                <select
                  className="letter-input letter-pick-select"
                  value={accDefaults.cities || ''}
                  onChange={(e) => pick('cities', e.target.value, 'city', 'value')}
                  aria-label={t('letters.city')}
                >
                  <option value="">{t('letters.pick')}</option>
                  {cities.map((x) => <option key={x.id} value={x.id}>{x.value}</option>)}
                </select>
                <button className="btn icon sm" onClick={() => saveToAccount('cities', 'city', false)} disabled={!draft.city.trim()} title={t('letters.save_to_account')} aria-label={t('letters.save_to_account')}>
                  <Icon name="plus" size={14} />
                </button>
              </div>
              <input
                className="letter-input"
                value={draft.city}
                onChange={(e) => {
                  cityTouched.current = true;
                  setField('city', e.target.value);
                }}
                placeholder={t('letters.city_ph')}
              />
            </Field>
            <Field label={t('letters.subject')}>
              <input
                className="letter-input"
                value={draft.subject}
                onChange={(e) => setField('subject', e.target.value)}
                placeholder={t('letters.subject_ph')}
              />
            </Field>
          </div>

          <Field label={t('letters.attachments')} hint={t('letters.attachments_hint')}>
            <div className="pj-list">
              {attachments.map((a, i) => (
                <div
                  key={a.id}
                  className={`pj-row ${dragIdx === i ? 'dragging' : ''}`}
                  draggable
                  onDragStart={() => setDragIdx(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragIdx !== null) reorderAttachment(dragIdx, i);
                    setDragIdx(null);
                  }}
                  onDragEnd={() => setDragIdx(null)}
                >
                  <span className="pj-grip" title={t('letters.drag_hint')} aria-hidden="true">
                    <Icon name="grip" size={15} />
                  </span>
                  <input
                    className="letter-input"
                    value={a.label}
                    onChange={(e) => updateAttachment(a.id, { label: e.target.value })}
                    placeholder={t('letters.attachments_ph')}
                  />
                  <label className={`btn sm pj-file ${a.file ? 'has-file' : ''}`}>
                    <Icon name="merge" size={14} />
                    <span className="pj-file-name">
                      {a.file ? a.file.name : t('letters.attach_file')}
                    </span>
                    <input
                      type="file"
                      accept="application/pdf,.pdf,image/*,.docx"
                      className="sr-only"
                      aria-label={t('letters.attach_file')}
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        updateAttachment(a.id, {
                          file: f,
                          label: a.label || (f ? f.name.replace(/\.[^.]+$/, '') : ''),
                        });
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    className="btn icon sm danger"
                    onClick={() => removeAttachment(a.id)}
                    aria-label={t('letters.remove_attachment')}
                    title={t('letters.remove_attachment')}
                  >
                    <Icon name="trash" size={13} />
                  </button>
                </div>
              ))}
              <div className="pj-add-row">
                <button
                  type="button"
                  className="btn sm primary"
                  onClick={() => pjFileRef.current?.click()}
                >
                  <Icon name="plus" size={14} /> {t('letters.add_attachment')}
                </button>
                <input
                  ref={pjFileRef}
                  type="file"
                  multiple
                  accept="application/pdf,.pdf,image/*,.docx"
                  className="sr-only"
                  aria-label={t('letters.add_attachment')}
                  onChange={(e) => {
                    addAttachmentFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
                <button type="button" className="btn sm" onClick={() => addAttachment()}>
                  <Icon name="text" size={14} /> {t('letters.add_attachment_text')}
                </button>
                {accAttachments.length > 0 && (
                  <select
                    className="letter-input letter-pick-select"
                    value=""
                    onChange={(e) => {
                      const it = accAttachments.find((x) => x.id === e.target.value);
                      if (it) addAttachment(it.value);
                    }}
                    aria-label={t('letters.pick_attachment')}
                  >
                    <option value="">{t('letters.pick_attachment')}</option>
                    {accAttachments.map((x) => (
                      <option key={x.id} value={x.id}>{x.value}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </Field>

          <Field label={t('letters.body')}>
            <textarea
              className="letter-input"
              rows={12}
              value={draft.body}
              onChange={(e) => setField('body', e.target.value)}
              placeholder={t('letters.body_ph')}
            />
          </Field>

          <Field label={t('letters.signature')}>
            <div className="letter-pick">
              <select
                className="letter-input letter-pick-select"
                value={accDefaults.signatures || ''}
                onChange={(e) => pick('signatures', e.target.value)}
                aria-label={t('letters.signature')}
              >
                <option value="">{t('letters.pick')}</option>
                {signatures.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
              </select>
              <button className="btn sm" onClick={() => setSigOpen(true)}>
                <Icon name="sign" size={14} /> {t('letters.add_signature')}
              </button>
            </div>
            <div className="letter-sig">
              {draft.signature ? (
                <img src={draft.signature.dataUrl} alt={t('letters.signature')} className="letter-sig-img" />
              ) : (
                <span className="tiny muted">{t('letters.no_signature')}</span>
              )}
              {draft.signature && (
                <button className="btn sm ghost" onClick={() => setSignature(null)}>
                  <Icon name="close" size={14} /> {t('letters.remove_signature')}
                </button>
              )}
            </div>
          </Field>

          {editingId && (
            <div className="letters-editing">
              <Icon name="text" size={13} />
              {t('letters.editing_entry')}
            </div>
          )}
          <div className="letters-actions">
            <button className="btn primary" onClick={generate} disabled={busy}>
              <Icon name="download" size={15} /> {t('letters.generate')}
            </button>
            <button className="btn" onClick={onSaveTemplate}>
              <Icon name="doc" size={15} /> {t('letters.save_template')}
            </button>
            <button className="btn ghost" onClick={newLetter}>
              <Icon name="reset" size={15} /> {t('letters.reset')}
            </button>
          </div>
      </div>

      <LetterHistoryModal
        open={histOpen}
        onClose={() => setHistOpen(false)}
        onRegenerate={regenerate}
        onRename={onRename}
        busy={busy}
      />

      <SignatureModal
        open={sigOpen}
        onClose={() => setSigOpen(false)}
        onSave={(sig) => {
          setSignature(sig);
          const label = window.prompt(t('account.signature_name'), 'Signature');
          if (label !== null) {
            addItem('signatures', { label: (label || 'Signature').trim(), dataUrl: sig.dataUrl, w: sig.w, h: sig.h });
          }
          setSigOpen(false);
          push({ message: t('letters.toast_signature'), kind: 'success' });
        }}
      />
    </div>
  );
}
